export type LookupSearchConditionOperator =
  | 'equalsIgnoreCase'
  | 'notFalse'
  | 'dateAfter'
  | 'dateOnOrBefore'
  | 'hccEquals';

export interface LookupSearchCondition {
  fields: string[];
  operator: LookupSearchConditionOperator;
  value?: string | number | Date | null;
  allowMissing?: boolean;
}

function normalizeHcc(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? `s:${raw.toLowerCase()}` : `n:${numeric}`;
}

function conditionMatches(item: Record<string, unknown>, condition: LookupSearchCondition): boolean {
  const value = condition.fields.map((field) => item[field]).find((fieldValue) => fieldValue != null);
  if (value == null) return condition.allowMissing ?? false;

  switch (condition.operator) {
    case 'notFalse':
      return value !== false;
    case 'equalsIgnoreCase':
      return String(value).trim().toLowerCase() === String(condition.value ?? '').trim().toLowerCase();
    case 'dateAfter':
      return new Date(value as string | number | Date).setHours(0, 0, 0, 0) > new Date(condition.value!).setHours(0, 0, 0, 0);
    case 'dateOnOrBefore':
      return new Date(value as string | number | Date).setHours(0, 0, 0, 0) <= new Date(condition.value!).setHours(0, 0, 0, 0);
    case 'hccEquals':
      return normalizeHcc(value) === normalizeHcc(condition.value);
  }
}

export function matchesLookupSearch(
  item: Record<string, unknown>,
  query: string,
  searchFields: string[],
  conditions?: LookupSearchCondition[]
): boolean {
  if (conditions && !conditions.every((condition) => conditionMatches(item, condition))) return false;
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const searchableText = searchFields.map((field) => String(item[field] ?? '')).join(' ').toLowerCase();
  return terms.every((term) => searchableText.includes(term));
}